import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import Button from "../../common/components/Button";
import DateRangeSelector from "../../common/components/DateRangeSelector";
import PageHeader from "../../common/components/PageHeader";
import { toast } from "../../common/components/Toast";
import { getReportTemplates } from "../api/report-templates";
import { createReport } from "../api/reports";

const CreateNewReport = () => {
  const { t } = useTranslation("reports");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setPeriodStart(params.get("period_start") || "");
    setPeriodEnd(params.get("period_end") || "");
  }, [location.search]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await getReportTemplates();
        setTemplates(response);
      } catch (error) {
        toast.error(t("createReport.messages.loadTemplatesError"));
      }
    };
    fetchTemplates();
  }, [t]);

  const handleCreateReport = async () => {
    const payload = {
      period_start: periodStart,
      period_end: periodEnd,
      template: selectedTemplate || null,
    };

    try {
      await createReport(payload);
      navigate("/reports");
      toast.success(t("createReport.messages.success"));
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Box>
      <PageHeader
        title={t("createReport.title")}
        appendActions={<DateRangeSelector />}
        description={t("createReport.description")}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <FormControl
          fullWidth
          margin="normal"
          sx={{ minWidth: 300, width: "50%" }}
        >
          <InputLabel id="template-label">
            {t("createReport.selectTemplateLabel")}
          </InputLabel>
          <Select
            labelId="template-label"
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            label={t("createReport.selectTemplateLabel")}
          >
            <MenuItem
              onClick={() => navigate("/report-templates/create")}
              sx={{
                color: "primary.main",
                fontWeight: 500,
              }}
            >
              <Plus size={18} style={{ marginRight: ".6rem" }} />{" "}
              {t("createReport.createNewTemplate")}
            </MenuItem>
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.title || t("templates.untitled")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateReport}
          sx={{ mt: 1 }}
          disabled={!selectedTemplate}
        >
          {t("createReport.submitRequest")}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateNewReport;
